import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Briefcase,
  Plus,
  Trash2,
  ArrowLeft,
  ArrowRight,
  Building2,
  UserCircle2,
  Calendar,
  ListChecks,
  PlusCircle,
  X,
  SkipForward,
} from "lucide-react";

export type Step5ExperienceRow = {
  organization: string;
  position: string;
  start_date: string;
  end_date?: string;
  responsibilities?: string;
};

export default function Step5Experience({
  onSubmit,
  busy,
  onBack,
  initialValues,
  readOnly,
}: {
  busy: boolean;
  onBack: () => void;
  readOnly?: boolean;
  initialValues?: { experiences: Step5ExperienceRow[] } | null;
  onSubmit: (experiences: Step5ExperienceRow[]) => Promise<void>;
}) {
  const emptyRow: Step5ExperienceRow = useMemo(
    () => ({
      organization: "",
      position: "",
      start_date: "",
      end_date: "",
      responsibilities: "",
    }),
    []
  );

  const [rows, setRows] = useState<Step5ExperienceRow[]>([emptyRow]);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!initialValues || hydratedRef.current) return;
    const incoming = initialValues.experiences || [];
    if (incoming.length === 0) return;

    hydratedRef.current = true;
    setRows(
      incoming.map((x) => ({
        organization: x.organization ?? "",
        position: x.position ?? "",
        start_date: x.start_date ?? "",
        end_date: x.end_date ?? "",
        responsibilities: x.responsibilities ?? "",
      }))
    );
  }, [initialValues]);

  const setRow = (i: number, k: keyof Step5ExperienceRow, v: string) => {
    setRows((s) => s.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  };

  return (
    <form
      className="space-y-8 pb-10"
      onSubmit={async (e) => {
        e.preventDefault();
        await onSubmit(rows);
      }}
    >
      {/* Header */}
      <div className="border-b border-gray-100 pb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-900 rounded-xl">
            <Briefcase className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Work Experience</h2>
            <p className="text-sm text-gray-500">
              Provide a chronological history of your professional career.{" "}
              <span className="text-gray-400">(Optional — you may skip this step)</span>
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-10">
        {rows.map((r, i) => (
          <div
            key={i}
            className="relative bg-white border border-gray-200 rounded-3xl p-6 transition-all hover:border-gray-300"
          >
            {/* Entry Number & Delete */}
            <div className="flex items-center justify-between mb-6">
              <span className="text-[10px] font-bold bg-gray-100 text-gray-500 px-3 py-1 rounded-full uppercase tracking-widest">
                Position #{i + 1} {!r.end_date && r.start_date && "• Current"}
              </span>
              {rows.length > 1 && (
                <button
                  type="button"
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  onClick={() =>
                    setRows((x) => x.filter((_, idx) => idx !== i))
                  }
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <Field
                label="Organization"
                icon={<Building2 className="w-3 h-3" />}
                value={r.organization}
                placeholder="e.g. KPMG Nigeria"
                onChange={(v: string) => setRow(i, "organization", v)}
              />
              <Field
                label="Position / Role"
                icon={<UserCircle2 className="w-3 h-3" />}
                value={r.position}
                placeholder="e.g. Senior Auditor"
                onChange={(v: string) => setRow(i, "position", v)}
              />
              <Field
                label="Start Date"
                icon={<Calendar className="w-3 h-3" />}
                type="date"
                value={r.start_date}
                onChange={(v: string) => setRow(i, "start_date", v)}
              />
              <Field
                label="End Date (Leave blank if current)"
                icon={<Calendar className="w-3 h-3" />}
                type="date"
                value={r.end_date || ""}
                onChange={(v: string) => setRow(i, "end_date", v)}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-2 tracking-tight">
                <ListChecks className="w-3 h-3" /> Core Responsibilities
              </label>
              <textarea
                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-gray-100 focus:bg-white focus:border-gray-400 transition-all min-h-[120px] resize-none"
                placeholder="Describe your key achievements and duties..."
                value={r.responsibilities || ""}
                onChange={(e) => setRow(i, "responsibilities", e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="flex items-center gap-2 px-5 py-2.5 text-gray-900 border-2 border-dashed border-gray-300 rounded-2xl text-xs font-bold hover:border-gray-900 transition-all"
        onClick={() => setRows((s) => [...s, { ...emptyRow }])}
      >
        <PlusCircle className="w-4 h-4" />
        Add Work Experience
      </button>

      {!readOnly && (
        <div className="flex justify-between items-center pt-8 border-t border-gray-100">
          <button
            type="button"
            disabled={busy}
            onClick={onBack}
            className="flex items-center gap-2 px-6 py-3 border border-gray-200 rounded-xl font-bold text-sm text-gray-600 hover:bg-gray-50 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={async () => onSubmit([])}
              className="flex items-center gap-2 px-6 py-3 border border-dashed border-gray-300 rounded-xl font-bold text-sm text-gray-400 hover:border-gray-500 hover:text-gray-600 transition-all"
            >
              <SkipForward className="w-4 h-4" />
              Skip
            </button>

            <button
              disabled={busy}
              className="group flex items-center gap-3 px-8 py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg shadow-gray-200"
            >
              {busy ? "Processing..." : "Save & Continue"}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      )}
    </form>
  );
}

/* ---------- Sub-components with Explicit Typing ---------- */

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  icon?: React.ReactNode;
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  icon,
}: FieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-2 tracking-tight">
        {icon} {label}
      </label>
      <input
        type={type}
        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100 focus:bg-white focus:border-gray-400 transition-all"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
